// purchases.ts — in-app purchase via RevenueCat (native only).
//
// Every function is a clean no-op / rejection when SHOW_IAP is false (web, or native
// with no RevenueCat key), so nothing here can change today's behaviour in those
// builds. RevenueCat's appUserID is always the Supabase user id; we never purchase
// anonymously. The server (iap-sync / iap-webhook) writes profiles.entitlement from
// RevenueCat's authoritative state, and the app just re-reads it.
import { Capacitor } from '@capacitor/core';
import { Purchases, type PurchasesPackage } from '@revenuecat/purchases-capacitor';
import { REVENUECAT_KEY, SHOW_IAP, SUPABASE } from '@/config/supabase';
import { getSession, cachedSession } from '@/services/auth';

export interface MembershipOption {
  priceString: string;
  /** Numeric store price and currency, used only to work out the annual saving. */
  price: number;
  currencyCode: string;
  pricePerMonthString: string | null;
  periodLabel: string;
  pkg: PurchasesPackage;
}
export interface MembershipPackages {
  monthly: MembershipOption | null;
  annual: MembershipOption | null;
}
export type BuyResult = 'purchased' | 'cancelled';
export type StoreSource = 'apple' | 'google';

const APP_ID = 'org.renurture.app';
const ENTITLEMENT_ID = 'renurture_pro';

let configured = false;
let loggedInUid: string | null = null;
let loginInFlight: Promise<void> | null = null;

export async function initPurchases(): Promise<void> {
  if (!SHOW_IAP || configured) return;
  await Purchases.configure({ apiKey: REVENUECAT_KEY });
  configured = true;
}

export async function iapLogIn(uid: string): Promise<void> {
  if (!SHOW_IAP || !uid) return;
  await initPurchases();
  if (loggedInUid === uid) return;
  const p = (async () => {
    await Purchases.logIn({ appUserID: uid });
    loggedInUid = uid;
  })();
  loginInFlight = p;
  try { await p; } finally { if (loginInFlight === p) loginInFlight = null; }
}

/** Idempotent: no-op when already logged in as uid, otherwise (re)tries logIn. Safe to
 *  call before every purchase action so an earlier failed logIn can recover. */
export async function ensureIapLogin(uid: string | null | undefined): Promise<void> {
  if (!SHOW_IAP || !uid || uid === 'local') return;
  if (loggedInUid === uid) return;
  await iapLogIn(uid);
}

export async function iapLogOut(): Promise<void> {
  if (!SHOW_IAP || !configured) return;
  if (loginInFlight) await loginInFlight.catch(() => undefined);
  const had = loggedInUid;
  loggedInUid = null;
  if (!had) return; // RevenueCat rejects logOut for an anonymous user
  try { await Purchases.logOut(); } catch { /* already anonymous */ }
}

export async function getMembershipPackages(): Promise<MembershipPackages> {
  if (!SHOW_IAP) return { monthly: null, annual: null };
  await initPurchases();
  const offerings = await Purchases.getOfferings();
  const cur = offerings.current;
  const wrap = (pkg: PurchasesPackage | null | undefined, periodLabel: string): MembershipOption | null =>
    pkg ? {
      priceString: pkg.product.priceString,
      price: pkg.product.price,
      currencyCode: pkg.product.currencyCode,
      pricePerMonthString: pkg.product.pricePerMonthString ?? null,
      periodLabel,
      pkg,
    } : null;
  return { monthly: wrap(cur?.monthly, 'per month'), annual: wrap(cur?.annual, 'per year') };
}

function assertReady(): void {
  if (!SHOW_IAP) throw new Error('Purchases are not available.');
  const uid = cachedSession()?.user.userId;
  if (!uid || loggedInUid !== uid) throw new Error('Please sign in first.');
}

function isCancel(e: unknown): boolean {
  return !!e && typeof e === 'object' && (e as { userCancelled?: boolean | null }).userCancelled === true;
}

export async function buyPackage(pkg: PurchasesPackage): Promise<BuyResult> {
  assertReady();
  try {
    await Purchases.purchasePackage({ aPackage: pkg });
    return 'purchased';
  } catch (e) {
    if (isCancel(e)) return 'cancelled';
    throw e;
  }
}

/** Resolves true only when the restored customer has the renurture_pro entitlement active. */
export async function restore(): Promise<boolean> {
  assertReady();
  const res = await Purchases.restorePurchases();
  return !!res?.customerInfo?.entitlements?.active?.[ENTITLEMENT_ID];
}

/** Ask the server to re-read RevenueCat and update the profile. Resolves true when it says member. */
export async function syncEntitlement(): Promise<boolean> {
  if (!SHOW_IAP) return false;
  const token = (await getSession())?.accessToken;
  if (!token || token === 'local') return false;
  const res = await fetch(`${SUPABASE.url.replace(/\/$/, '')}/functions/v1/iap-sync`, {
    method: 'POST',
    headers: { authorization: `Bearer ${token}`, apikey: SUPABASE.anonKey, 'content-type': 'application/json' },
    body: '{}',
  });
  if (!res.ok) throw new Error('Sync failed');
  const json = (await res.json().catch(() => ({}))) as { member?: boolean };
  return !!json.member;
}

export function manageSubscriptionUrl(source: StoreSource): string {
  return source === 'apple'
    ? 'https://apps.apple.com/account/subscriptions'
    : `https://play.google.com/store/account/subscriptions?package=${APP_ID}`;
}

/** The store name to show for this device (copy only). */
export function storeName(): string {
  return Capacitor.getPlatform() === 'ios' ? 'App Store' : 'Google Play';
}

/** Developer diagnostics: what StoreKit/RevenueCat report for the store country and each
 *  plan's price (to explain a currency mismatch between the paywall and Apple's sheet). */
export async function storeDiagnostics(): Promise<string> {
  if (!SHOW_IAP) return 'In-app purchase is not available in this build.';
  try {
    await initPurchases();
    let country = 'unknown';
    try { country = (await Purchases.getStorefront()).countryCode; } catch { /* ignore */ }
    const cur = (await Purchases.getOfferings()).current;
    const line = (label: string, p: PurchasesPackage | null | undefined) =>
      p ? `${label}: ${p.product.currencyCode} ${p.product.price} "${p.product.priceString}" (${p.product.identifier})` : `${label}: not loaded`;
    return [`Store country: ${country}`, `Offering: ${cur?.identifier ?? 'none'}`, line('Monthly', cur?.monthly), line('Yearly', cur?.annual)].join(' · ');
  } catch (e) {
    return `Store check failed: ${e instanceof Error ? e.message : String(e)}`;
  }
}
